/**
 * The shell-agnostic half of the demo application.
 *
 * Everything a shell does *after* it has bytes is identical: open a session,
 * walk the manifest, stream chunks into the viewer, show diagnostics, pick, and
 * report typed failures. That is all here, so `demo/web/src/main.ts` and
 * `demo/tauri/src/main.ts` are left with only what genuinely differs — which
 * `MeshSource` to construct and how the user names a file.
 *
 * This module owns no policy about *where* bytes come from. It exposes
 * `openBytes` / `openBlob` and the shell calls one of them.
 *
 * ## DOM contract
 *
 * Both shells ship the same element ids; this class looks them up rather than
 * building markup, so each shell keeps full control of its page copy and
 * layout. Required ids:
 *
 * `canvas-host`, `status`, `error`, `drop-overlay`, `attribution`,
 * `fixture-select`, `class-summary`, `hierarchy`, `hierarchy-filter`,
 * `hierarchy-filter-clear`, `hierarchy-show-all`, `hierarchy-hide-all`, `hierarchy-isolate`,
 * `plan-storey`, `plan-canvas`, `plan-axis-legend`, `plan-metadata`, `plan-empty`, `view-reset`,
 * `focus-selection`,
 * `pick-identity`, `pick-instance`, `pick-storey`, `pick-chunk`, `pick-attributes`,
 * `pick-dimensions`, `pick-axis-length`, `pick-vertices`, `pick-triangles`, `pick-chunk-details`, `diag-shell`,
 * `diag-input`, `diag-input-check`, `diag-open`, `diag-chunks`,
 * `diag-vertices`, `diag-triangles`, `diag-failed`, `diag-guid-parse`,
 * `diag-stream`, `diag-model-bytes`, `diag-plan-bytes`, `diag-peak-bytes`,
 * `diag-peak-proj-bytes`, `diag-live-sessions`, `diag-live-models`, `diag-live-projections`,
 * `diag-retained-meshes`, `diag-retained-mesh-bytes`, `diag-retained-logical-bytes`,
 * `diag-cache-budget`, `diag-chunk-requests`, `diag-linear-bytes`,
 * `model-stats-elements`, `model-stats-chunks`, `model-stats-vertices`, `model-stats-triangles`,
 * `model-stats-dimensions`,
 * `georef-panel`,
 * `perf-fps`, `perf-load-time`, `perf-parse-rate`, `perf-wasm-memory`, `perf-wasm-bar`,
 * `class-color-toggle`, `class-color-legend`,
 * `sec-x-min`, `sec-x-max`, `sec-x-label`, `sec-y-min`, `sec-y-max`, `sec-y-label`,
 * `sec-z-min`, `sec-z-max`, `sec-z-label`, `sec-reset`,
 * `explode-slider`, `explode-value`, `explode-reset`,
 * `measure-start`, `measure-clear`, `measure-result`, `screenshot-button`, `fullscreen-button`.
 *
 * A missing id throws at construction time: a silently dead diagnostics row is
 * worse than a shell that refuses to start.
 *
 * `admission-note` was removed in 2026-08-07: the demo no longer states a limit
 * it has not measured anywhere in the visible UI. The one surviving limit
 * message fires from `openBytes()` only when a file actually exceeds the
 * provisional envelope, and lands in `diag-input-check` plus the status line.
 */

import type { ModelAttribution } from "./attribution";
import { MODEL_ATTRIBUTIONS, findAttribution, renderAttributionPanel } from "./attribution";
import { renderClassSummaryPanel } from "./class-summary";
import { PlanViewer } from "./plan-viewer";
import type { PlanProjectionChunkHeader, PlanProjectionManifest, PlanProjectionOpts } from "./types";
import type { HierarchyElement, HierarchyPanel, HierarchyStorey, ModelHierarchy } from "./hierarchy";
import { HierarchyIndex, renderHierarchyPanel } from "./hierarchy";
import { inspectInputBytes, looksLikeIfcSpf } from "./input-guard";
import { assertAxisLedgerAlignment, type MeshSource, type SessionHandle } from "./mesh-source";
import type { PickResult, StreamBudget, StreamProgress } from "./renderer";
import { Viewer, streamModel } from "./renderer";
import type {
  AxisLineEntry,
  AxisLinesTable,
  AxisOnlyEntry,
  AxisOnlyTable,
  ElementInfoEntry,
  ElementInfoTable,
  Georef,
} from "./types";
import { IfcEngineError, toEngineError } from "./types";

/**
 * Consumer-side streaming budget.
 *
 * TODO(M1, design doc §8-2): calibrate against measurement. These are holding
 * values chosen so the committed sample fixtures load whole; they are NOT a
 * claim about what a browser or a WebView can take.
 */
export const DEFAULT_STREAM_BUDGET: StreamBudget = {
  maxEstimatedVertices: 8_000_000,
  maxChunks: 512,
};

/** The engine's own input ceiling, quoted only to say what it is not. */
export const ENGINE_MAX_IFC_BYTES = 512 * 1024 * 1024;

export interface DemoAppOptions {
  readonly source: MeshSource;
  /** Defaults to {@link DEFAULT_STREAM_BUDGET}. */
  readonly budget?: StreamBudget;
  /**
   * Build and display source-plan projections. Defaults to `true`.
   *
   * Shells can disable the unfinished 2D panel without paying its projection
   * and chunk-hydration cost while the underlying engine candidate remains
   * available for tests and later UI work.
   */
  readonly enablePlanProjection?: boolean;
  /**
   * The demo's own input admission cap, or `null` to assert none.
   *
   * TODO(M1, design doc §8-2): still UNDECIDED, hence the `null` default. The
   * engine's `MAX_IFC_BYTES` (512 MiB) is a parse guard, not a memory cap — the
   * parsed model dominates, and product admission is `unknown`. Until a measured
   * value exists we show real numbers instead of a made-up limit.
   */
  readonly inputLimitBytes?: number | null;
  /**
   * Called once per open attempt, before any engine work. Shells use it to
   * dismiss start-up chrome (the native shell's splash) exactly when the user
   * has committed to a model, and not on mere hover or dialog cancel.
   */
  readonly onOpenStarted?: () => void;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KiB", "MiB", "GiB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 2 : 1)} ${units[unit]}`;
}

/** Same constant as the renderer: engine millimetres to scene metres. */
const MM_TO_M = 0.001;

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function formatDistance(metres: number): string {
  if (metres < 1) {
    return `${(metres * 100).toFixed(1)} cm`;
  }
  return `${metres.toFixed(3)} m`;
}

function formatDimensions(dx: number, dy: number, dz: number): string {
  return `${formatDistance(dx)} × ${formatDistance(dy)} × ${formatDistance(dz)}`;
}

function formatCoordinate(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 3, minimumFractionDigits: 3 });
}

function formatAngle(radians: number): string {
  const degrees = (radians * 180) / Math.PI;
  return degrees.toLocaleString("en-US", { maximumFractionDigits: 4, minimumFractionDigits: 4 });
}

function formatScale(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 6, minimumFractionDigits: 6 });
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** How many chunk ids the pick panel lists before it says "and N more". */
const MAX_LISTED_CHUNK_IDS = 4;

/** IFC class -> display colour used by the 3D class-colour mode. */
const CLASS_COLORS: Record<string, string> = {
  IfcWall: "#e57373",
  IfcWallStandardCase: "#ef5350",
  IfcSlab: "#81c784",
  IfcRoof: "#64b5f6",
  IfcDoor: "#ffb74d",
  IfcWindow: "#4fc3f7",
  IfcColumn: "#ba68c8",
  IfcBeam: "#9575cd",
  IfcStair: "#ff8a65",
  IfcRailing: "#a1887f",
  IfcFurnishingElement: "#90a4ae",
  IfcSpace: "#fff176",
  IfcBuildingElementProxy: "#bdbdbd",
};

/** Visual reference line shown by the WASM memory bar (50 MiB), not a limit. */
const WASM_MEMORY_REFERENCE_BYTES = 50 * 1024 * 1024;

/** `chunk_id` list for the pick panel, capped so a spread element stays readable. */
function formatChunkIds(chunkIds: readonly number[]): string {
  if (chunkIds.length === 0) {
    return "—";
  }
  if (chunkIds.length <= MAX_LISTED_CHUNK_IDS) {
    return chunkIds.join(", ");
  }
  const head = chunkIds.slice(0, MAX_LISTED_CHUNK_IDS).join(", ");
  return `${head}, +${formatCount(chunkIds.length - MAX_LISTED_CHUNK_IDS)}`;
}

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (found === null) {
    throw new Error(`missing element #${id}`);
  }
  return found as T;
}

/**
 * What the "selected element" panel shows, from either selection route.
 *
 * A canvas pick and a tree click resolve to the same thing — one
 * `instanceIndex` and the chunks that carry it — so they share one code path
 * and cannot drift into showing different identities for the same element.
 */
interface Selection {
  readonly instanceIndex: number;
  readonly identity: string | null;
  readonly storeyLevel: number;
  readonly storeyName: string | null;
  /** Every loaded chunk carrying a triangle of this element. */
  readonly chunkIds: readonly number[];
  /** Null when element_info was not loaded or the index is out of range. */
  readonly elementInfo: ElementInfoEntry | null;
}

export class DemoApp {
  private readonly source: MeshSource;
  private readonly budget: StreamBudget;
  private readonly inputLimitBytes: number | null;
  private readonly onOpenStarted: (() => void) | null;
  private readonly planProjectionEnabled: boolean;

  private readonly viewer: Viewer;
  private readonly status = el<HTMLParagraphElement>("status");
  private readonly errorBox = el<HTMLPreElement>("error");
  private readonly dropOverlay = el<HTMLDivElement>("drop-overlay");
  private readonly attributionPanel = el<HTMLElement>("attribution");
  private readonly fixtureSelect = el<HTMLSelectElement>("fixture-select");
  private readonly hierarchyHost = el<HTMLElement>("hierarchy");
  private readonly hierarchyFilter = el<HTMLInputElement>("hierarchy-filter");
  private hierarchyFilterQuery = "";
  private readonly hierarchyShowAll = el<HTMLButtonElement>("hierarchy-show-all");
  private readonly hierarchyHideAll = el<HTMLButtonElement>("hierarchy-hide-all");
  private readonly hierarchyIsolate = el<HTMLButtonElement>("hierarchy-isolate");
  private readonly classSummaryHost = el<HTMLElement>("class-summary");
  private readonly modelStatsElements = el<HTMLElement>("model-stats-elements");
  private readonly modelStatsChunks = el<HTMLElement>("model-stats-chunks");
  private readonly modelStatsVertices = el<HTMLElement>("model-stats-vertices");
  private readonly modelStatsTriangles = el<HTMLElement>("model-stats-triangles");
  private readonly modelStatsDimensions = el<HTMLElement>("model-stats-dimensions");
  private readonly modelStatsUnmeshed = el<HTMLElement>("model-stats-unmeshed");
  private readonly georefPanel = el<HTMLElement>("georef-panel");
  private readonly classColorToggle = el<HTMLInputElement>("class-color-toggle");
  private readonly classColorLegend = el<HTMLElement>("class-color-legend");
  private readonly attributesHost = el<HTMLElement>("pick-attributes");
  private readonly pickDimensions = el<HTMLElement>("pick-dimensions");
  private readonly pickAxisLength = el<HTMLElement>("pick-axis-length");
  private readonly pickVertices = el<HTMLElement>("pick-vertices");
  private readonly pickTriangles = el<HTMLElement>("pick-triangles");
  private readonly chunkDetailsHost = el<HTMLElement>("pick-chunk-details");
  private readonly planStoreySelect = el<HTMLSelectElement>("plan-storey");
  private readonly planCanvas = el<HTMLCanvasElement>("plan-canvas");
  private planViewer: PlanViewer | null = null;
  private planProjection: PlanProjectionManifest | null = null;
  private currentPlanChunkId: number | null = null;
  private planChunkRequestId = 0;
  private readonly planMetadataPanel = el<HTMLElement>("plan-metadata");
  private readonly planEmpty = el<HTMLElement>("plan-empty");
  private readonly planAxisLegend = el<HTMLElement>("plan-axis-legend");
  private planCanvasHost: HTMLElement | null = null;
  private planResizeObserver: ResizeObserver | null = null;
  private readonly focusButton = el<HTMLButtonElement>("focus-selection");

  private handle: SessionHandle | null = null;
  /** The element the pick panel currently describes, for the focus affordance. */
  private selection: Selection | null = null;
  /** Dense instanceIndex → element info table, or null when not loaded. */
  private elementInfoTable: ElementInfoTable | null = null;
  /** Axis-only products of the loaded model (null until loaded / unsupported). */
  private axisOnlyTable: AxisOnlyTable | null = null;
  /** Drawable/refused source Axis rows, separate from the complete C ledger. */
  private axisLinesTable: AxisLinesTable | null = null;
  private selectedAxisExpressId: number | null = null;
  private axisOnlyButtons = new Map<number, HTMLButtonElement>();
  /** Current model's georeferencing block, or null before the first model. */
  private currentGeoref: Georef | null = null;
  /** 3D No-Silent-Loss ledger from the manifest (null until a model loads). */
  private currentUnmeshed: {
    total: number;
    byClass: Readonly<Record<string, number>>;
    axisOnly: number;
    axisOnlyByClass: Readonly<Record<string, number>>;
    withoutRepresentation: number;
  } | null = null;
  private streaming: AbortController | null = null;
  /** Guards against a slow load finishing after a newer one started. */
  private generation = 0;
  /** The current model's tree, or `null` before the first model finishes loading. */
  private hierarchy: ModelHierarchy | null = null;
  private hierarchyPanel: HierarchyPanel | null = null;

  private readonly perfFps = el<HTMLElement>("perf-fps");
  private readonly perfLoadTime = el<HTMLElement>("perf-load-time");
  private readonly perfParseRate = el<HTMLElement>("perf-parse-rate");
  private readonly perfWasmMemory = el<HTMLElement>("perf-wasm-memory");
  private readonly perfWasmBar = el<HTMLDivElement>("perf-wasm-bar");
  private loadStartedAtMs: number | null = null;
  private lastProgress: StreamProgress | null = null;
  private lastLinearBytes = 0;
  private lastLinearUpdateAt = 0;
  private linearMemoryPolling = false;
  private lastRenderedFps = -1;
  private lastRenderedLinearBytes = -1;

  private readonly sectionInputs = {
    x: { min: el<HTMLInputElement>("sec-x-min"), max: el<HTMLInputElement>("sec-x-max"), label: el<HTMLElement>("sec-x-label") },
    y: { min: el<HTMLInputElement>("sec-y-min"), max: el<HTMLInputElement>("sec-y-max"), label: el<HTMLElement>("sec-y-label") },
    z: { min: el<HTMLInputElement>("sec-z-min"), max: el<HTMLInputElement>("sec-z-max"), label: el<HTMLElement>("sec-z-label") },
  } as const;
  private readonly sectionReset = el<HTMLButtonElement>("sec-reset");
  private readonly explodeSlider = el<HTMLInputElement>("explode-slider");
  private readonly explodeValue = el<HTMLElement>("explode-value");
  private readonly explodeReset = el<HTMLButtonElement>("explode-reset");

  private readonly measureStart = el<HTMLButtonElement>("measure-start");
  private readonly measureClear = el<HTMLButtonElement>("measure-clear");
  private readonly measureResult = el<HTMLElement>("measure-result");
  private readonly screenshotButton = el<HTMLButtonElement>("screenshot-button");
  private readonly fullscreenButton = el<HTMLButtonElement>("fullscreen-button");
  private readonly loadProgressOverlay: HTMLElement;
  private readonly loadProgressBar: HTMLElement;
  private readonly loadProgressLabel: HTMLElement;
  private measureMode = false;

  constructor(options: DemoAppOptions) {
    this.source = options.source;
    this.budget = options.budget ?? DEFAULT_STREAM_BUDGET;
    this.inputLimitBytes = options.inputLimitBytes ?? null;
    this.onOpenStarted = options.onOpenStarted ?? null;
    this.planProjectionEnabled = options.enablePlanProjection ?? true;

    const canvasHost = el<HTMLElement>("canvas-host");
    this.viewer = new Viewer(canvasHost, { grid: true });
    this.loadProgressOverlay = document.createElement("div");
    this.loadProgressOverlay.className = "load-progress-overlay";
    this.loadProgressOverlay.hidden = true;
    const loadProgressTrack = document.createElement("div");
    loadProgressTrack.className = "load-progress-track";
    this.loadProgressBar = document.createElement("div");
    this.loadProgressBar.className = "load-progress-bar";
    this.loadProgressLabel = document.createElement("div");
    this.loadProgressLabel.className = "load-progress-label";
    loadProgressTrack.append(this.loadProgressBar);
    this.loadProgressOverlay.append(loadProgressTrack, this.loadProgressLabel);
    canvasHost.append(this.loadProgressOverlay);

    el("diag-shell").textContent = this.source.shell;
    this.resetPerfMeter();
    this.startPerfLoop();
    this.wireSectionBox();
    this.wireExplodeView();
    this.applyClassColors();
    this.wireClassColorToggle();
    this.wireKeyboardShortcuts();

    this.buildFixturePicker();
    this.wireDragAndDrop();
    this.wirePicking();
    el<HTMLButtonElement>("view-reset").addEventListener("click", () => {
      this.viewer.resetView();
      this.hierarchyPanel?.showAllStoreys();
    });
    this.focusButton.addEventListener("click", () => {
      this.focusSelection();
    });
    this.measureStart.addEventListener("click", () => {
      this.startMeasure();
    });
    this.measureClear.addEventListener("click", () => {
      this.clearMeasure();
    });
    this.screenshotButton.addEventListener("click", () => {
      this.takeScreenshot();
    });
    this.fullscreenButton.addEventListener("click", () => {
      this.toggleFullscreen();
    });
    const projectionButton = el<HTMLButtonElement>("toggle-projection");
    projectionButton.addEventListener("click", () => {
      this.viewer.toggleProjection();
      projectionButton.textContent = this.viewer.isOrtho ? "透視投影 / Perspective" : "平行投影 / Orthographic";
    });
    projectionButton.textContent = "平行投影 / Orthographic";
    this.wireHierarchyFilter();
    this.wireStoreyVisibilityControls();
    this.hierarchyShowAll.disabled = true;
    this.hierarchyHideAll.disabled = true;
    this.hierarchyIsolate.disabled = true;
    this.wirePlanStoreySelect();
    if (this.planProjectionEnabled) {
      this.planViewer = new PlanViewer(this.planCanvas, {
        onAxisSelect: (line) => {
          const ledgerEntry = this.axisOnlyTable?.entries.find(
            (entry) => entry.express_id === line.identity.express_id,
          );
          if (ledgerEntry !== undefined) {
            this.selectAxisOnly(ledgerEntry);
          }
        },
      });
      this.planCanvasHost = this.planCanvas.parentElement;
      this.wirePlanCanvasResize();
    }
    this.select(null, { focus: false });
    this.resetHierarchy();
    renderAttributionPanel(this.attributionPanel, null);
  }

  /** Clear the fixture picker's selection — a shell-supplied file is not a fixture. */
  clearFixtureSelection(): void {
    this.fixtureSelect.value = "";
  }

  private buildFixturePicker(): void {
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "選択してください";
    this.fixtureSelect.append(placeholder);

    for (const attribution of MODEL_ATTRIBUTIONS) {
      const option = document.createElement("option");
      option.value = attribution.id;
      // Every entry in `MODEL_ATTRIBUTIONS` is currently bundled, so this branch
      // is not reached today. It stays because the table is the place a future
      // model gets registered, and a model listed before its redistributable
      // bytes exist must be visibly unavailable rather than fetch a 404.
      option.disabled = attribution.fixturePath === null;
      option.textContent =
        attribution.fixturePath === null ? `${attribution.label}（未同梱）` : attribution.label;
      this.fixtureSelect.append(option);
    }

    this.fixtureSelect.addEventListener("change", () => {
      const attribution = findAttribution(this.fixtureSelect.value);
      if (attribution?.fixturePath == null) {
        return;
      }
      void this.openFixture(attribution);
    });
  }

  /**
   * Window-level HTML5 drag and drop.
   *
   * Works unchanged in the native shell because its window sets
   * `dragDropEnabled: false`, which hands drops to the WebView instead of the
   * OS-level handler. With the default `true` no `drop` event ever reaches this
   * listener.
   */
  private wireDragAndDrop(): void {
    let depth = 0;
    const show = (visible: boolean): void => {
      this.dropOverlay.hidden = !visible;
    };

    window.addEventListener("dragenter", (event) => {
      event.preventDefault();
      depth += 1;
      show(true);
    });
    window.addEventListener("dragover", (event) => {
      // Without this the WebView navigates to the file instead of dropping it.
      event.preventDefault();
    });
    window.addEventListener("dragleave", (event) => {
      event.preventDefault();
      depth = Math.max(0, depth - 1);
      if (depth === 0) {
        show(false);
      }
    });
    window.addEventListener("drop", (event) => {
      event.preventDefault();
      depth = 0;
      show(false);
      const file = event.dataTransfer?.files.item(0);
      if (file == null) {
        return;
      }
      this.clearFixtureSelection();
      void this.openBlob(file.name, file);
    });
  }

  /**
   * Canvas selection.
   *
   * A single click **selects only**: it highlights the element and fills the
   * pick panel, and the camera does not move. Clicking around a model is how a
   * user inspects it, and a camera that flies on every click makes inspection
   * fight navigation. Moving the camera stays an explicit gesture — the
   * `選択にフォーカス` button, a double-click, or a tree click.
   */
  private wirePicking(): void {
    const host = el<HTMLElement>("canvas-host");
    let downX = 0;
    let downY = 0;

    host.addEventListener("pointerdown", (event) => {
      downX = event.clientX;
      downY = event.clientY;
    });
    host.addEventListener("pointerup", (event) => {
      // Anything beyond a few pixels was an orbit drag, not a pick.
      if (Math.hypot(event.clientX - downX, event.clientY - downY) > 4) {
        return;
      }
      if (this.measureMode) {
        this.addMeasurePoint(event.clientX, event.clientY);
        return;
      }
      this.select(this.fromPick(this.viewer.pick(event.clientX, event.clientY)), {
        focus: false,
      });
    });
    // The second click of a double-click has already selected through the
    // handler above, so this only has to act on whatever is selected now.
    host.addEventListener("dblclick", () => {
      this.focusSelection();
    });
  }

  /**
   * Fly to the current selection, if it has loaded geometry to fly to.
   *
   * Shared by the `選択にフォーカス` button and the canvas double-click so both
   * routes cannot drift.
   */
  private focusSelection(): void {
    const selection = this.selection;
    if (selection === null) {
      return;
    }
    this.viewer.focusInstance(selection.instanceIndex, selection.chunkIds);
  }

  /** Enter measurement mode: the next two canvas clicks become measure points. */
  private startMeasure(): void {
    this.viewer.startMeasure();
    this.measureMode = true;
    this.measureResult.textContent = "3D 上で 1 点目をクリック / Click the first point in 3D";
    this.measureStart.disabled = true;
    this.measureClear.disabled = false;
  }

  /** Add a canvas click as a measure point when in measure mode. */
  private addMeasurePoint(clientX: number, clientY: number): void {
    const point = this.viewer.pickPoint(clientX, clientY);
    if (point === null) {
      this.measureResult.textContent = "面上ではなく空間をクリックしました / Clicked empty space";
      return;
    }
    const { count, distance } = this.viewer.addMeasurePoint(point);
    if (count === 1) {
      this.measureResult.textContent = "2 点目をクリック / Click the second point";
    } else if (count === 2 && distance !== null) {
      this.measureResult.textContent = `距離 / Distance: ${formatDistance(distance)}`;
      this.measureMode = false;
      this.measureStart.disabled = false;
    }
  }

  /** Clear the measurement overlay and exit measure mode. */
  private clearMeasure(): void {
    this.viewer.clearMeasure();
    this.measureMode = false;
    this.measureResult.textContent = "—";
    this.measureStart.disabled = false;
    this.measureClear.disabled = true;
  }

  /** Capture the current 3D canvas as a PNG and trigger a download. */
  private takeScreenshot(): void {
    const canvas = this.viewer.canvas;
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    const now = new Date();
    const stamp =
      `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-` +
      `${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;
    link.download = `ifc-engine-core-screenshot-${stamp}.png`;
    document.body.append(link);
    link.click();
    link.remove();
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  }

  /** Widen a raycast hit to every chunk the element occupies. */
  private fromPick(hit: PickResult | null): Selection | null {
    if (hit === null) {
      return null;
    }
    const info = this.elementInfoTable?.element_info[hit.instanceIndex] ?? null;
    return {
      instanceIndex: hit.instanceIndex,
      identity: hit.identity,
      storeyLevel: hit.storeyLevel,
      storeyName: hit.storeyName,
      // The hit chunk is only the one the ray struck; an element sub-split
      // across chunks needs all of them for its bounds to be its real bounds.
      chunkIds: this.hierarchy?.chunksByInstance.get(hit.instanceIndex) ?? [hit.chunkId],
      elementInfo: info,
    };
  }

  /**
   * Show a selection, highlight it in the 3D view, and mark it in the tree.
   *
   * The single entry point for both selection routes (canvas pick and tree
   * click), so the two cannot drift into showing different identities for the
   * same element. What differs is `focus`: a tree click is an explicit
   * navigation gesture and moves the camera, a canvas click is not and does not.
   *
   * Highlighting and focusing are both skipped when the element's vertices are
   * not loaded — over budget, or in a chunk that failed to build — rather than
   * washing or framing a box that describes nothing on screen. The focus
   * affordance is disabled in exactly that case, since it would do nothing.
   */
  private select(selection: Selection | null, options: { readonly focus: boolean }): void {
    this.selection = selection;
    this.selectedAxisExpressId = null;
    this.planViewer?.selectAxisLine(null);
    this.updateAxisOnlySelectionStyles();
    const info = selection?.elementInfo ?? null;
    el("pick-identity").textContent = selection?.identity ?? "—";
    el("pick-name").textContent = info?.name ?? "—";
    el("pick-class").textContent = info?.ifc_class ?? "—";
    el("pick-description").textContent = info?.description ?? "—";
    el("pick-instance").textContent =
      selection === null ? "—" : String(selection.instanceIndex);
    el("pick-storey").textContent =
      selection === null
        ? "—"
        : `${selection.storeyLevel}${selection.storeyName === null ? "" : ` (${selection.storeyName})`}`;
    el("pick-chunk").textContent =
      selection === null ? "—" : formatChunkIds(selection.chunkIds);

    if (selection === null) {
      this.pickDimensions.textContent = "—";
      this.pickAxisLength.textContent = "—";
      this.pickVertices.textContent = "—";
      this.pickTriangles.textContent = "—";
    } else {
      const bounds = this.viewer.instanceBoundsMm(selection.instanceIndex, selection.chunkIds);
      const geom = this.viewer.instanceGeometryCount(selection.instanceIndex, selection.chunkIds);
      this.pickDimensions.textContent =
        bounds === null
          ? "—"
          : formatDimensions(
              (bounds[3] - bounds[0]) * MM_TO_M,
              (bounds[4] - bounds[1]) * MM_TO_M,
              (bounds[5] - bounds[2]) * MM_TO_M,
            );
      this.pickAxisLength.textContent = "—";
      this.pickVertices.textContent = geom === null ? "—" : formatCount(geom.vertices);
      this.pickTriangles.textContent = geom === null ? "—" : formatCount(geom.triangles);
    }

    this.renderAttributes(info, selection?.instanceIndex ?? null);
    this.renderChunkDetails(selection);

    this.hierarchyPanel?.highlight(selection?.instanceIndex ?? null);
    this.syncPlanStoreyToSelection(selection);

    let rendered = false;
    if (selection === null) {
      this.viewer.clearHighlight();
    } else {
      rendered = this.viewer.highlightInstance(selection.instanceIndex, selection.chunkIds);
    }
    this.focusButton.disabled = !rendered;
    this.hierarchyIsolate.disabled = this.hierarchy === null || selection === null;

    if (selection !== null && options.focus) {
      this.viewer.focusInstance(selection.instanceIndex, selection.chunkIds);
    }
  }

  /** Empty the tree back to its placeholder — a new model owns the panel from here. */
  private resetHierarchy(): void {
    this.hierarchy = null;
    this.hierarchyPanel = null;
    this.hierarchyHost.replaceChildren();
    const placeholder = document.createElement("p");
    placeholder.className = "hint";
    placeholder.textContent = "モデルを開くと階層が表示されます。/ Open a model to see its hierarchy.";
    this.hierarchyHost.append(placeholder);
  }

  private renderChunkDetails(selection: Selection | null): void {
    if (selection === null) {
      this.chunkDetailsHost.replaceChildren();
      const placeholder = document.createElement("p");
      placeholder.className = "hint";
      placeholder.textContent = "要素を選択するとチャンク詳細が表示されます。/ Select an element to see chunk details.";
      this.chunkDetailsHost.append(placeholder);
      return;
    }

    const details = this.viewer.instanceChunkDetails(selection.instanceIndex, selection.chunkIds);
    if (details.length === 0) {
      this.chunkDetailsHost.replaceChildren();
      const empty = document.createElement("p");
      empty.className = "hint";
      empty.textContent = "この要素のチャンク情報は読み込まれていません。/ No chunk data loaded for this element.";
      this.chunkDetailsHost.append(empty);
      return;
    }

    const list = document.createElement("dl");
    list.className = "kv";
    for (const chunk of details) {
      const row = document.createElement("div");
      row.className = "kv-row";
      const dt = document.createElement("dt");
      dt.textContent = `chunk ${chunk.chunkId}`;
      const dd = document.createElement("dd");
      const originM = chunk.quantOriginMm.map((mm) => (mm * MM_TO_M).toFixed(3));
      const storey = chunk.storeyName === null ? `L${chunk.storeyLevel}` : `${chunk.storeyLevel} (${chunk.storeyName})`;
      dd.textContent = `${storey} · origin (${originM.join(", ")}) m · scale ${chunk.quantScaleMm.toExponential(2)} · ${formatCount(chunk.vertices)}v / ${formatCount(chunk.triangles)}t`;
      row.append(dt, dd);
      list.append(row);
    }
    this.chunkDetailsHost.replaceChildren(list);
  }

  private renderClassSummary(): void {
    const info = this.elementInfoTable;
    if (info === null) {
      this.classSummaryHost.replaceChildren();
      const placeholder = document.createElement("p");
      placeholder.className = "hint";
      placeholder.textContent = "モデルを開くとクラス一覧が表示されます。/ Open a model to see its class summary.";
      this.classSummaryHost.append(placeholder);
      return;
    }

    renderClassSummaryPanel(this.classSummaryHost, info, {
      onSelectClass: (className: string) => {
        this.hierarchyFilter.value = className;
        this.applyHierarchyFilter(className);
      },
    });
    const axisOnly = this.axisOnlyTable;
    if (axisOnly !== null && axisOnly.entries.length > 0) {
      const note = document.createElement("p");
      note.className = "hint";
      note.textContent =
        `うち Axis only / Body なし: ${formatCount(axisOnly.entries.length)} 件` +
        "（下の階層ツリー末尾に一覧があります）";
      this.classSummaryHost.append(note);
    }
  }

  private renderModelStats(): void {
    const info = this.elementInfoTable;
    if (info === null) {
      this.modelStatsElements.textContent = "—";
      this.modelStatsChunks.textContent = "—";
      this.modelStatsVertices.textContent = "—";
      this.modelStatsTriangles.textContent = "—";
      this.modelStatsDimensions.textContent = "—";
      this.modelStatsUnmeshed.textContent = "—";
      return;
    }

    this.modelStatsElements.textContent = formatCount(info.entry_count);
    const geom = this.viewer.modelGeometryStats();
    this.modelStatsChunks.textContent = formatCount(this.viewer.loadedChunkCount);
    this.modelStatsVertices.textContent = formatCount(geom.vertices);
    this.modelStatsTriangles.textContent = formatCount(geom.triangles);

    if (this.viewer.loadedChunkCount === 0) {
      this.modelStatsDimensions.textContent = "—";
    } else {
      const bounds = this.viewer.modelBoundsM;
      this.modelStatsDimensions.textContent = formatDimensions(
        bounds.max[0] - bounds.min[0],
        bounds.max[1] - bounds.min[1],
        bounds.max[2] - bounds.min[2],
      );
    }

    this.renderUnmeshed();
  }

  /** Render the 3D No-Silent-Loss ledger: elements the mesh path cannot show. */
  private renderUnmeshed(): void {
    const ledger = this.currentUnmeshed;
    if (ledger === null) {
      this.modelStatsUnmeshed.textContent = "—";
      return;
    }
    if (ledger.total === 0 && ledger.withoutRepresentation === 0 && ledger.axisOnly === 0) {
      this.modelStatsUnmeshed.textContent = "0（全要素を表示）";
      this.modelStatsUnmeshed.title = "";
      return;
    }
    const parts: string[] = [];
    if (ledger.total > 0) {
      parts.push(`${formatCount(ledger.total)} 件（未対応ジオメトリ）`);
    }
    if (ledger.axisOnly > 0) {
      parts.push(`${formatCount(ledger.axisOnly)} 件（軸線のみ・Body なし）`);
    }
    if (ledger.withoutRepresentation > 0) {
      parts.push(`${formatCount(ledger.withoutRepresentation)} 件（表現参照なし）`);
    }
    this.modelStatsUnmeshed.textContent = parts.join(" + ");
    const detail = Object.entries(ledger.byClass)
      .map(([cls, count]) => `${cls}: ${formatCount(count)}`)
      .join("\n");
    const axisDetail = Object.entries(ledger.axisOnlyByClass)
      .map(([cls, count]) => `${cls}: ${formatCount(count)}`)
      .join("\n");
    this.modelStatsUnmeshed.title =
      `表示されない要素（クラス別）\n${detail}` +
      (ledger.axisOnly > 0 ? `\n軸線のみ（Body なし）\n${axisDetail}` : "") +
      (ledger.withoutRepresentation > 0
        ? `\n表現参照なし: ${formatCount(ledger.withoutRepresentation)}`
        : "");
  }

  /** Render the georeferencing block, or a clear "no map conversion" fallback. */
  private renderGeoref(): void {
    const g = this.currentGeoref;
    this.georefPanel.textContent = "";
    if (g === null || !g.has_map_conversion) {
      const placeholder = document.createElement("p");
      placeholder.className = "hint";
      placeholder.textContent =
        "このモデルには地図変換情報（IfcMapConversion）がありません。" +
        "No map conversion / georeferencing data in this model.";
      this.georefPanel.append(placeholder);
      return;
    }

    const list = document.createElement("dl");
    list.className = "kv";

    const crsName = [g.crs_name, g.crs_vertical].filter(Boolean).join(" / ") || "—";
    list.append(this.kvRow("CRS", crsName));
    if (g.crs_description) {
      list.append(this.kvRow("CRS description", g.crs_description));
    }
    list.append(this.kvRow("Eastings", `${formatCoordinate(g.eastings)} m`));
    list.append(this.kvRow("Northings", `${formatCoordinate(g.northings)} m`));
    list.append(this.kvRow("Orthogonal height", `${formatCoordinate(g.orthogonal_height)} m`));

    const rotation =
      g.x_axis_abscissa === 0 && g.x_axis_ordinate === 0
        ? "—"
        : `${formatAngle(Math.atan2(g.x_axis_ordinate, g.x_axis_abscissa))}°`;
    list.append(this.kvRow("Rotation", rotation));
    list.append(this.kvRow("Scale", formatScale(g.scale)));

    this.georefPanel.append(list);
  }

  private kvRow(label: string, value: string): HTMLElement {
    const row = document.createElement("div");
    row.className = "kv-row";
    const dt = document.createElement("dt");
    dt.textContent = label;
    const dd = document.createElement("dd");
    dd.textContent = value;
    row.append(dt, dd);
    return row;
  }

  /** Push the static class palette to the viewer. Call once at construction. */
  private applyClassColors(): void {
    const map = new Map<string, string>();
    for (const [cls, color] of Object.entries(CLASS_COLORS)) {
      map.set(cls, color);
    }
    this.viewer.setClassColors(map);
  }

  private wireClassColorToggle(): void {
    this.classColorToggle.addEventListener("change", () => {
      this.viewer.setClassColorEnabled(this.classColorToggle.checked);
    });
  }

  /** Global keyboard shortcuts; ignored when the user is typing in a form control. */
  private wireKeyboardShortcuts(): void {
    const isTyping = (event: KeyboardEvent): boolean => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return false;
      }
      return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable
      );
    };

    window.addEventListener("keydown", (event) => {
      if (isTyping(event)) {
        return;
      }
      // Let browser shortcuts (copy, reload, etc.) pass through.
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      switch (event.key) {
        case "Escape": {
          event.preventDefault();
          if (this.measureMode) {
            this.clearMeasure();
          } else {
            this.select(null, { focus: false });
          }
          break;
        }
        case "f":
        case "F": {
          if (event.shiftKey) {
            event.preventDefault();
            this.toggleFullscreen();
          } else {
            event.preventDefault();
            this.focusSelection();
          }
          break;
        }
        case "m":
        case "M": {
          event.preventDefault();
          if (this.measureMode) {
            this.clearMeasure();
          } else {
            this.startMeasure();
          }
          break;
        }
        case "c":
        case "C": {
          event.preventDefault();
          this.classColorToggle.checked = !this.classColorToggle.checked;
          this.classColorToggle.dispatchEvent(new Event("change"));
          break;
        }
        case "o":
        case "O": {
          event.preventDefault();
          el<HTMLButtonElement>("toggle-projection").click();
          break;
        }
        case "r":
        case "R":
        case "Home": {
          event.preventDefault();
          this.viewer.resetView();
          this.hierarchyPanel?.showAllStoreys();
          break;
        }
        case "p":
        case "P": {
          event.preventDefault();
          this.takeScreenshot();
          break;
        }
      }
    });
  }

  /** Render every property from `element_info` in the selection panel. */
  private renderAttributes(
    info: ElementInfoEntry | null,
    instanceIndex: number | null,
  ): void {
    this.attributesHost.replaceChildren();

    if (info === null) {
      const empty = document.createElement("p");
      empty.className = "attr-empty";
      empty.textContent =
        instanceIndex === null
          ? "要素を選択すると属性が表示されます。/ Select an element to see attributes."
          : "この要素の属性情報は読み込まれていません。/ No attribute information for this element.";
      this.attributesHost.append(empty);
      return;
    }

    const displayNames: Record<string, string> = {
      name: "name",
      ifc_class: "ifc_class",
      description: "description",
    };

    const list = document.createElement("dl");
    list.className = "attr-list";

    for (const [rawKey, value] of Object.entries(info)) {
      const key = displayNames[rawKey] ?? rawKey;
      const row = document.createElement("div");
      row.className = "attr-row";

      const keyEl = document.createElement("dt");
      keyEl.textContent = key;

      const valueEl = document.createElement("dd");
      valueEl.textContent = value ?? "—";
      if (value === null) {
        valueEl.className = "attr-empty";
      }

      row.append(keyEl, valueEl);
      list.append(row);
    }

    this.attributesHost.append(list);
  }

  /** Build the small legend under the class-colour toggle from the loaded table. */
  private renderClassColorLegend(): void {
    const info = this.elementInfoTable;
    if (info === null) {
      this.classColorLegend.replaceChildren();
      const placeholder = document.createElement("p");
      placeholder.className = "hint";
      placeholder.textContent = "モデルを開くと凡例が表示されます。/ Open a model to see the legend.";
      this.classColorLegend.append(placeholder);
      return;
    }

    const counts = new Map<string, number>();
    for (const entry of info.element_info) {
      const cls = entry.ifc_class;
      if (cls !== null && CLASS_COLORS[cls] !== undefined) {
        counts.set(cls, (counts.get(cls) ?? 0) + 1);
      }
    }

    const list = document.createElement("ul");
    list.className = "class-color-legend";

    const sorted = [...counts.entries()].sort((a, b) => {
      if (b[1] !== a[1]) {
        return b[1] - a[1];
      }
      return a[0].localeCompare(b[0]);
    });

    for (const [className, count] of sorted) {
      const item = document.createElement("li");
      item.className = "class-color-legend-item";

      const swatch = document.createElement("span");
      swatch.className = "class-color-legend-swatch";
      swatch.style.backgroundColor = CLASS_COLORS[className] ?? "#9e9e9e";

      const label = document.createElement("span");
      label.className = "class-color-legend-name";
      label.textContent = className;

      const countEl = document.createElement("span");
      countEl.className = "class-color-legend-count";
      countEl.textContent = count.toLocaleString("en-US");

      item.append(swatch, label, countEl);
      list.append(item);
    }

    this.classColorLegend.replaceChildren();
    if (list.children.length === 0) {
      const none = document.createElement("p");
      none.className = "hint";
      none.textContent = "凡例に対応する IFC class がありません。/ No mapped IFC classes.";
      this.classColorLegend.append(none);
    } else {
      this.classColorLegend.append(list);
    }
  }

  private wireHierarchyFilter(): void {
    const clear = el<HTMLButtonElement>("hierarchy-filter-clear");
    let timeout: ReturnType<typeof setTimeout> | null = null;

    this.hierarchyFilter.addEventListener("input", () => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        this.applyHierarchyFilter(this.hierarchyFilter.value);
      }, 150);
    });

    clear.addEventListener("click", () => {
      this.hierarchyFilter.value = "";
      this.applyHierarchyFilter("");
      this.hierarchyFilter.focus();
    });
  }

  /** Bulk visibility controls for storeys: show all, hide all, isolate selection's storey. */
  private wireStoreyVisibilityControls(): void {
    this.hierarchyShowAll.addEventListener("click", () => {
      if (this.hierarchy === null) {
        return;
      }
      for (const storey of this.hierarchy.storeys) {
        this.viewer.setChunksVisible(storey.chunkIds, true);
      }
      this.hierarchyPanel?.showAllStoreys();
    });

    this.hierarchyHideAll.addEventListener("click", () => {
      if (this.hierarchy === null) {
        return;
      }
      for (const storey of this.hierarchy.storeys) {
        this.viewer.setChunksVisible(storey.chunkIds, false);
      }
      this.hierarchyPanel?.hideAllStoreys();
    });

    this.hierarchyIsolate.addEventListener("click", () => {
      if (this.hierarchy === null || this.selection === null) {
        return;
      }
      const level = this.selection.storeyLevel;
      for (const storey of this.hierarchy.storeys) {
        this.viewer.setChunksVisible(storey.chunkIds, storey.level === level);
      }
      this.hierarchyPanel?.isolateStorey(level);
    });
  }

  private applyHierarchyFilter(query: string): void {
    this.hierarchyFilterQuery = query.trim();
    if (this.hierarchy === null) {
      return;
    }

    const info = this.elementInfoTable;
    const q = this.hierarchyFilterQuery.toLowerCase();
    const filter =
      q.length === 0
        ? undefined
        : {
            matches: (element: HierarchyElement, label: string | null): boolean => {
              if (label !== null && label.toLowerCase().includes(q)) {
                return true;
              }
              if (element.identity !== null && element.identity.toLowerCase().includes(q)) {
                return true;
              }
              if (info === null) {
                return false;
              }
              const entry = info.element_info[element.instanceIndex];
              if (entry === undefined) {
                return false;
              }
              if (entry.ifc_class !== null && entry.ifc_class.toLowerCase().includes(q)) {
                return true;
              }
              if (entry.name !== null && entry.name.toLowerCase().includes(q)) {
                return true;
              }
              if (entry.description !== null && entry.description.toLowerCase().includes(q)) {
                return true;
              }
              return false;
            },
          };

    this.hierarchyPanel = renderHierarchyPanel(this.hierarchyHost, this.hierarchy, {
      elementLabel:
        info === null
          ? undefined
          : (element) => {
              const entry = info.element_info[element.instanceIndex];
              if (entry === undefined || entry.name === null) {
                return null; // fall back to GUID
              }
              if (entry.ifc_class === null) {
                return entry.name;
              }
              return `${entry.name}（${entry.ifc_class}）`;
            },
      filter,
      onSelectElement: (element: HierarchyElement, storey: HierarchyStorey) => {
        // Clicking a row in the tree is navigation, not inspection: the user
        // asked to be taken to an element they cannot necessarily see yet.
        const info = this.elementInfoTable?.element_info[element.instanceIndex] ?? null;
        this.select(
          {
            instanceIndex: element.instanceIndex,
            identity: element.identity,
            storeyLevel: storey.level,
            storeyName: storey.name,
            chunkIds: element.chunkIds,
            elementInfo: info,
          },
          { focus: true },
        );
      },
      onStoreyVisibilityChanged: (storey: HierarchyStorey, visible: boolean) => {
        this.viewer.setChunksVisible(storey.chunkIds, visible);
      },
    });
    this.renderAxisOnlyGroup();
  }

  /** Append the axis-only group below the tree (C increment of the staged D). */
  private renderAxisOnlyGroup(): void {
    const table = this.axisOnlyTable;
    if (table === null || table.entries.length === 0) {
      return;
    }
    const group = document.createElement("details");
    group.className = "axis-only-group";
    const summary = document.createElement("summary");
    summary.textContent = `Axis only / Body なし（${formatCount(table.entries.length)} 件）`;
    group.append(summary);
    const list = document.createElement("ul");
    list.className = "axis-only-list";
    this.axisOnlyButtons.clear();
    for (const entry of table.entries) {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "axis-only-entry";
      const line = this.axisLinesTable?.entries.find(
        (candidate) => candidate.identity.express_id === entry.express_id,
      );
      const refusal = this.axisLinesTable?.refusals.find(
        (candidate) => candidate.identity.express_id === entry.express_id,
      );
      const label =
        `${entry.ifc_class} #${entry.express_id}` +
        (!this.planProjectionEnabled
          ? " — 3D Body なし"
          : line === undefined
          ? ` — 2D非表示（${refusal?.reason ?? "axis binding not closed"}）`
          : " — 2D Axis");
      const icon = document.createElement("span");
      icon.className =
        "axis-only-entry-icon " +
        (!this.planProjectionEnabled
          ? ""
          : line === undefined
          ? "axis-only-entry-icon-refused"
          : "axis-only-entry-icon-line");
      icon.setAttribute("aria-hidden", "true");
      if (!this.planProjectionEnabled) {
        icon.textContent = "—";
      } else if (line === undefined) {
        icon.textContent = "×";
      }
      const labelNode = document.createElement("span");
      labelNode.textContent = label;
      button.append(icon, labelNode);
      button.title = !this.planProjectionEnabled
        ? "Axis only / Body なし — 2D panel is temporarily disabled"
        : line === undefined
          ? `Axis only / Body なし — ${refusal?.reason ?? "axis binding not closed"}`
          : `2D source Axis · Axis length ${formatCoordinate(line.axis_length_mm)} mm`;
      button.addEventListener("click", () => this.selectAxisOnly(entry));
      this.axisOnlyButtons.set(entry.express_id, button);
      item.append(button);
      list.append(item);
    }
    group.append(list);
    this.hierarchyHost.append(group);
    this.updateAxisOnlySelectionStyles();
  }

  /** Show an axis-only element in the selection detail with its badge. */
  private selectAxisOnly(entry: AxisOnlyEntry): void {
    const line = this.axisLinesTable?.entries.find(
      (candidate) => candidate.identity.express_id === entry.express_id,
    );
    this.selection = null;
    this.selectedAxisExpressId = entry.express_id;
    this.viewer.clearHighlight();
    this.hierarchyPanel?.highlight(null);
    el("pick-identity").textContent =
      `#${entry.express_id} · live session/source record`;
    el("pick-name").textContent = "—";
    el("pick-class").textContent = entry.ifc_class;
    el("pick-description").textContent =
      "Axis only / Body なし — source に solid がなく、3D 表示されません。" +
      (!this.planProjectionEnabled
        ? " The unfinished 2D panel is temporarily disabled."
        : line === undefined
        ? " Axis binding is not closed, so no 2D line is drawn."
        : " The dashed 2D line is the source Axis, not a physical product outline.");
    el("pick-instance").textContent = "—";
    el("pick-storey").textContent = line === undefined ? "—" : String(line.storey_level);
    el("pick-chunk").textContent = "—";
    this.pickDimensions.textContent = "—";
    this.pickAxisLength.textContent =
      line === undefined ? "—" : `${formatCoordinate(line.axis_length_mm)} mm (source Axis)`;
    this.pickVertices.textContent = "—";
    this.pickTriangles.textContent = "—";
    this.focusButton.disabled = true;
    this.hierarchyIsolate.disabled = true;
    this.renderAttributes(null, null);
    this.renderChunkDetails(null);
    this.planViewer?.selectAxisLine(entry.express_id);
    this.updateAxisOnlySelectionStyles();
    if (line !== undefined) {
      const chunkId = line.storey_level - 1;
      if (this.planProjection?.chunks.some((chunk) => chunk.chunkId === chunkId)) {
        this.planStoreySelect.value = String(chunkId);
        void this.renderPlanChunk(chunkId);
      }
    }
  }

  private updateAxisOnlySelectionStyles(): void {
    for (const [expressId, button] of this.axisOnlyButtons) {
      const selected = expressId === this.selectedAxisExpressId;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", String(selected));
    }
  }

  private showHierarchy(hierarchy: ModelHierarchy): void {
    this.hierarchy = hierarchy;
    this.hierarchyShowAll.disabled = hierarchy.storeys.length === 0;
    this.hierarchyHideAll.disabled = hierarchy.storeys.length === 0;
    this.hierarchyIsolate.disabled = hierarchy.storeys.length === 0 || this.selection === null;
    this.applyHierarchyFilter(this.hierarchyFilterQuery);
  }

  private wirePlanStoreySelect(): void {
    this.planStoreySelect.addEventListener("change", () => {
      const chunkId = parseInt(this.planStoreySelect.value, 10);
      if (!Number.isNaN(chunkId)) {
        void this.renderPlanChunk(chunkId);
      }
    });
  }

  private wirePlanCanvasResize(): void {
    if (this.planCanvasHost === null || typeof ResizeObserver === "undefined") {
      return;
    }
    this.planResizeObserver = new ResizeObserver(() => {
      this.planViewer?.redraw();
    });
    this.planResizeObserver.observe(this.planCanvasHost);
  }

  private wireSectionBox(): void {
    for (const axis of ["x", "y", "z"] as const) {
      const { min, max } = this.sectionInputs[axis];
      min.addEventListener("input", () => this.updateSectionBox());
      max.addEventListener("input", () => this.updateSectionBox());
    }
    this.sectionReset.addEventListener("click", () => this.resetSectionBox());
    this.resetSectionBox();
  }

  private wireExplodeView(): void {
    this.explodeSlider.addEventListener("input", () => {
      const value = parseFloat(this.explodeSlider.value);
      this.viewer.setExplode(value / 100);
      this.explodeValue.textContent = `${value}%`;
    });
    this.explodeReset.addEventListener("click", () => this.resetExplodeView());
  }

  private updateSectionBox(): void {
    const bounds = this.viewer.modelBoundsM;
    const min: [number, number, number] = [
      this.sectionValue("x", "min", bounds.min[0], bounds.max[0]),
      this.sectionValue("y", "min", bounds.min[1], bounds.max[1]),
      this.sectionValue("z", "min", bounds.min[2], bounds.max[2]),
    ];
    const max: [number, number, number] = [
      this.sectionValue("x", "max", bounds.min[0], bounds.max[0]),
      this.sectionValue("y", "max", bounds.min[1], bounds.max[1]),
      this.sectionValue("z", "max", bounds.min[2], bounds.max[2]),
    ];

    // Clamp so a min slider cannot cross past its max slider.
    for (let i = 0; i < 3; i += 1) {
      if (min[i] > max[i]) {
        const mid = (min[i] + max[i]) / 2;
        min[i] = mid;
        max[i] = mid;
      }
    }

    this.viewer.setSectionBox(min, max);
    this.updateSectionLabels(min, max);
  }

  private sectionValue(
    axis: "x" | "y" | "z",
    end: "min" | "max",
    boundsMin: number,
    boundsMax: number,
  ): number {
    const input = this.sectionInputs[axis][end];
    const pct = clamp(parseFloat(input.value) / 100, 0, 1);
    return lerp(boundsMin, boundsMax, pct);
  }

  private updateSectionLabels(
    min?: readonly [number, number, number],
    max?: readonly [number, number, number],
  ): void {
    const bounds = this.viewer.modelBoundsM;
    const axisNames = ["x", "y", "z"] as const;
    for (let i = 0; i < 3; i += 1) {
      const axis = axisNames[i];
      const lo = min?.[i] ?? this.sectionValue(axis, "min", bounds.min[i], bounds.max[i]);
      const hi = max?.[i] ?? this.sectionValue(axis, "max", bounds.min[i], bounds.max[i]);
      this.sectionInputs[axis].label.textContent = `${lo.toFixed(2)} ~ ${hi.toFixed(2)} m`;
    }
  }

  private resetSectionBox(): void {
    for (const axis of ["x", "y", "z"] as const) {
      this.sectionInputs[axis].min.value = "0";
      this.sectionInputs[axis].max.value = "100";
    }
    this.viewer.clearSectionBox();
    this.updateSectionLabels();
  }

  private resetExplodeView(): void {
    this.explodeSlider.value = "0";
    this.explodeValue.textContent = "0%";
    this.viewer.setExplode(0);
  }

  private async createPlanProjection(handle: SessionHandle): Promise<void> {
    const generation = this.generation;
    const opts: PlanProjectionOpts = {
      version: 1,
      cut_height_mm: 1000,
      cut_band_mm: 100,
      view_below_mm: 0,
      view_above_mm: 5000,
      tolerance_mm: 5,
      hidden_lines: "none",
      include_mep: false,
      mep_representation: "centerline",
    };

    try {
      const manifest = await this.source.planProjectionManifest(handle, opts);
      if (generation !== this.generation) {
        await this.source.closePlanProjection(handle, manifest.projectionId);
        return;
      }
      this.planProjection = manifest;
      this.buildPlanStoreyOptions();
      const firstChunk = this.planProjection.chunks[0];
      if (firstChunk !== undefined) {
        this.planStoreySelect.value = String(firstChunk.chunkId);
        await this.renderPlanChunk(firstChunk.chunkId);
      }
    } catch (cause: unknown) {
      if (generation !== this.generation) {
        return;
      }
      this.planViewer?.clear();
      console.error(toEngineError("source_plan_projection_manifest", cause));
    }
  }

  private buildPlanStoreyOptions(): void {
    this.planStoreySelect.replaceChildren();
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "選択してください";
    this.planStoreySelect.append(placeholder);

    for (const chunk of this.planProjection?.chunks ?? []) {
      const option = document.createElement("option");
      option.value = String(chunk.chunkId);
      option.textContent = chunk.storey.name ?? `level ${chunk.chunkId}`;
      this.planStoreySelect.append(option);
    }
  }

  private async renderPlanChunk(chunkId: number): Promise<void> {
    if (this.handle === null || this.planProjection === null || this.planViewer === null) {
      return;
    }
    if (this.currentPlanChunkId === chunkId) {
      return;
    }
    this.currentPlanChunkId = chunkId;
    const requestId = ++this.planChunkRequestId;
    const generation = this.generation;

    try {
      const chunk = await this.source.planProjectionChunk(
        this.handle,
        this.planProjection.projectionId,
        chunkId,
      );
      if (generation !== this.generation || requestId !== this.planChunkRequestId) {
        return;
      }
      const axisLines =
        this.axisLinesTable?.entries.filter((entry) => entry.storey_level === chunkId + 1) ?? [];
      this.planViewer.setAxisLines(axisLines, this.selectedAxisExpressId);
      this.planEmpty.hidden = chunk.header.primitives.length > 0 || axisLines.length > 0;
      if (chunk.header.primitives.length === 0 && axisLines.length === 0) {
        this.planViewer.clear();
      } else {
        this.planViewer.render(chunk);
      }
      this.renderPlanMetadata(chunk.header);
      this.renderAxisLegend(axisLines);
    } catch (cause: unknown) {
      if (generation !== this.generation || requestId !== this.planChunkRequestId) {
        return;
      }
      this.currentPlanChunkId = null;
      this.planViewer.clear();
      this.planEmpty.hidden = true;
      this.renderPlanMetadata(null);
      this.renderAxisLegend([]);
      console.error(toEngineError("source_plan_projection_chunk", cause));
    }
  }

  private renderAxisLegend(lines: readonly AxisLineEntry[]): void {
    this.planAxisLegend.replaceChildren();
    const table = this.axisLinesTable;
    if (table === null) {
      return;
    }
    const swatch = document.createElement("span");
    swatch.className = "plan-axis-legend-swatch";
    const label = document.createElement("span");
    label.textContent =
      `Source Axis（破線）: ${formatCount(lines.length)} 件 / ` +
      `refused: ${formatCount(table.refused_entry_count)} 件`;
    this.planAxisLegend.append(swatch, label);
  }

  private renderPlanMetadata(header: PlanProjectionChunkHeader | null): void {
    this.planMetadataPanel.textContent = "";
    if (header === null) {
      return;
    }

    const list = document.createElement("dl");
    list.className = "kv";

    const options = this.planProjection?.options;
    if (options !== undefined) {
      list.append(this.kvRow("cut height", `${formatCoordinate(options.cut_height_mm)} mm`));
      list.append(this.kvRow("cut band", `${formatCoordinate(options.cut_band_mm)} mm`));
      list.append(this.kvRow("view below", `${formatCoordinate(options.view_below_mm)} mm`));
      list.append(this.kvRow("view above", `${formatCoordinate(options.view_above_mm)} mm`));
      list.append(this.kvRow("tolerance", `${formatCoordinate(options.tolerance_mm)} mm`));
      list.append(this.kvRow("hidden lines", options.hidden_lines));
      list.append(this.kvRow("include MEP", options.include_mep ? "yes" : "no"));
    }

    const storey = header.storey;
    list.append(this.kvRow("storey", storey.name ?? `level ${header.chunkId}`));
    list.append(this.kvRow("elevation", `${formatCoordinate(storey.elevationMm)} mm`));
    list.append(this.kvRow("express id", storey.expressId));
    if (storey.guid) {
      list.append(this.kvRow("guid", storey.guid));
    }

    const acc = header.accounting;
    list.append(this.kvRow("candidates", formatCount(acc.sourceCandidates)));
    list.append(this.kvRow("emitted", formatCount(acc.emittedSources)));
    list.append(this.kvRow("omitted", formatCount(acc.omittedSources)));
    list.append(this.kvRow("excluded", formatCount(acc.excludedOutsideRange)));

    if (header.omissions.length > 0) {
      const omissionsHeading = document.createElement("p");
      omissionsHeading.className = "panel-subheading";
      omissionsHeading.textContent = `Omissions (${formatCount(header.omissions.length)})`;
      this.planMetadataPanel.append(omissionsHeading);

      const omissionsList = document.createElement("ul");
      omissionsList.className = "plan-omissions";
      const MAX_OMISSIONS = 20;
      for (const omission of header.omissions.slice(0, MAX_OMISSIONS)) {
        const item = document.createElement("li");
        item.textContent = `${omission.ifcClass}: ${omission.reason}`;
        omissionsList.append(item);
      }
      if (header.omissions.length > MAX_OMISSIONS) {
        const item = document.createElement("li");
        item.textContent = `… and ${formatCount(header.omissions.length - MAX_OMISSIONS)} more`;
        omissionsList.append(item);
      }
      this.planMetadataPanel.append(list, omissionsHeading, omissionsList);
      return;
    }

    this.planMetadataPanel.append(list);
  }

  private syncPlanStoreyToSelection(selection: Selection | null): void {
    if (selection === null || this.planProjection === null) {
      return;
    }
    // Mesh chunk storey_level is -based; projection chunk_id is 0-based.
    const chunkId = selection.storeyLevel - 1;
    const chunk = this.planProjection.chunks.find((c) => c.chunkId === chunkId);
    if (chunk !== undefined) {
      this.planStoreySelect.value = String(chunk.chunkId);
      void this.renderPlanChunk(chunk.chunkId);
    }
  }

  /** Fetch and open a bundled fixture, wiring up its attribution panel. */
  async openFixture(attribution: ModelAttribution): Promise<void> {
    const path = attribution.fixturePath;
    if (path === null) {
      return;
    }
    this.setStatus(`${attribution.label} を取得中…`);
    try {
      const response = await fetch(new URL(path, window.location.href));
      if (!response.ok) {
        throw new Error(`fixture fetch failed: ${response.status} ${response.statusText}`);
      }
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (!looksLikeIfcSpf(bytes)) {
        throw new Error(
          `fixture ${path} is not an IFC-SPF file (no ISO-10303-21 header); ` +
            "the bundled fixture is probably missing",
        );
      }
      await this.openBytes(attribution.title, bytes, attribution);
    } catch (cause: unknown) {
      this.fail(toEngineError("fixture_fetch", cause));
    }
  }

  /** Open a `File` / `Blob` the shell obtained from an input or a drop. */
  async openBlob(name: string, blob: Blob): Promise<void> {
    this.setStatus(`${name} を読み込み中…`);
    try {
      await this.openBytes(name, new Uint8Array(await blob.arrayBuffer()), null);
    } catch (cause: unknown) {
      this.fail(toEngineError("read_file", cause));
    }
  }

  async openBytes(
    name: string,
    input: Uint8Array,
    attribution: ModelAttribution | null,
  ): Promise<void> {
    this.onOpenStarted?.();
    this.generation += 1;
    const generation = this.generation;

    // Cancel any load still streaming; a newer request always wins.
    this.streaming?.abort();
    const controller = new AbortController();
    this.streaming = controller;
    const signal = controller.signal;

    this.clearError();
    this.viewer.clear();
    this.select(null, { focus: false });
    this.resetHierarchy();
    this.elementInfoTable = null;
    this.axisOnlyTable = null;
    this.axisLinesTable = null;
    this.selectedAxisExpressId = null;
    this.axisOnlyButtons.clear();
    this.currentGeoref = null;
    this.currentUnmeshed = null;
    this.renderClassSummary();
    this.renderModelStats();
    this.renderGeoref();
    this.classColorToggle.checked = false;
    this.renderClassColorLegend();
    this.planProjection = null;
    this.currentPlanChunkId = null;
    this.planChunkRequestId += 1;
    this.planViewer?.clear();
    this.renderAxisLegend([]);
    this.planEmpty.hidden = true;
    this.renderPlanMetadata(null);
    this.planStoreySelect.replaceChildren();
    this.lastProgress = null;
    this.resetPerfMeter();
    this.resetSectionBox();
    this.resetExplodeView();
    this.clearMeasure();
    renderAttributionPanel(this.attributionPanel, attribution);
    el("diag-input").textContent = `${name} — ${formatBytes(input.byteLength)}`;

    await this.closeCurrentSession();
    this.loadStartedAtMs = performance.now();
    this.showLoadProgressOverlay();

    // Viewer-side pre-checks, before the engine is called at all. A rejection
    // here is not an engine failure and must not be dressed up as one: the
    // engine's own words still reach the user unchanged whenever it is the
    // engine that fails (below, via `toEngineError`).
    const guard = inspectInputBytes(input);
    el("diag-input-check").textContent = guard.note;
    if (!guard.ok) {
      this.fail(new IfcEngineError("input_check", guard.message, guard.detail));
      return;
    }
    const bytes = guard.bytes;

    // The cap is advisory, not an admission rule: it was measured in Node, not a
    // browser, and admission is officially unknown — so warn and continue.
    //
    // The warning is written to `diag-input-check` (a persistent diagnostics
    // row), not only `setStatus()`: the status line is overwritten by
    // "open_ifc…" a few lines below with no `await` in between, so a
    // status-only warning would never survive to paint.
    if (this.inputLimitBytes !== null && bytes.byteLength > this.inputLimitBytes) {
      const warning =
        `注意: ${formatBytes(bytes.byteLength)} は実測済みの安全域（暫定 ` +
        `${formatBytes(this.inputLimitBytes)}・Node 実測）を超えています。続行します… / ` +
        `Warning: ${formatBytes(bytes.byteLength)} exceeds the measured envelope; continuing.`;
      el("diag-input-check").textContent = `${guard.note} — ${warning}`;
      this.setStatus(warning);
    }

    // Captured locally so a failure can only ever close the session it opened,
    // never whichever session happens to be current when it lands.
    let opened: SessionHandle | null = null;

    try {
      this.setStatus("open_ifc…");
      // `loadStartedAtMs` is captured after `closeCurrentSession` so the load
      // time reported in the performance meter does not include tearing down the
      // previous session.
      const startedAt = performance.now();
      const handle = await this.source.open(bytes);
      const openMs = performance.now() - startedAt;
      opened = handle;

      if (generation !== this.generation) {
        await this.source.close(handle);
        return;
      }
      this.handle = handle;
      el("diag-open").textContent = `${openMs.toFixed(1)} ms`;

      const manifest = await this.source.manifest(handle);
      if (generation !== this.generation) {
        return;
      }
      this.currentGeoref = manifest.georef;
      this.currentUnmeshed = {
        total: manifest.unmeshed_geometry_products,
        byClass: manifest.unmeshed_geometry_by_class,
        axisOnly: manifest.axis_only_geometry_products,
        axisOnlyByClass: manifest.axis_only_by_class,
        withoutRepresentation: manifest.geometry_products_without_representation,
      };
      this.renderGeoref();
      el("diag-chunks").textContent = `${formatCount(manifest.chunks.length)} planned`;

      // Load the additive element-info table before chunk streaming, so the
      // tree and pick panel have names from the first selection. A build
      // without the candidate feature returns null: the panel falls back to
      // GUIDs gracefully.
      this.elementInfoTable = await this.source.elementInfo(handle);
      if (generation !== this.generation) {
        return;
      }
      this.axisOnlyTable = await this.source.axisOnly(handle);
      if (generation !== this.generation) {
        return;
      }
      if (this.planProjectionEnabled) {
        this.axisLinesTable = await this.source.axisLines(handle);
        if (generation !== this.generation) {
          return;
        }
        assertAxisLedgerAlignment(this.axisOnlyTable, this.axisLinesTable);
      } else {
        this.axisLinesTable = null;
      }
      this.renderClassSummary();
      this.renderModelStats();
      this.viewer.setInstanceClassBulk(this.elementInfoTable);
      this.renderClassColorLegend();
      if (this.planProjectionEnabled) {
        await this.createPlanProjection(handle);
        if (generation !== this.generation) {
          return;
        }
      }

      // A structurally valid IFC can carry no geometric representation at all
      // (`ifc43-bsi-gherkin-blt003-stair.ifc` is exactly that: an IfcStair
      // aggregating an IfcStairFlight, with no IfcProductDefinitionShape). The
      // plan is then legitimately empty. Saying so beats an unexplained blank
      // canvas that reads as a broken viewer.
      if (manifest.chunks.length === 0) {
        this.showHierarchy(new HierarchyIndex().build([]));
        const drawableAxes = this.planProjectionEnabled
          ? (this.axisLinesTable?.drawable_entry_count ?? 0)
          : 0;
        this.setStatus(
          drawableAxes > 0
            ? `3D Body はありません。2D source Axis を ${formatCount(drawableAxes)} 件表示できます。`
            : "このモデルには描画可能なジオメトリがありません" +
                "（manifest の chunk 数が 0。IFC としては妥当で、表現形状を持たないだけです）。",
        );
        el("diag-stream").textContent = "no renderable geometry";
        await this.refreshSessionDiagnostics(handle);
        this.updateLoadMetrics();
        this.resetSectionBox();
        return;
      }

      this.setStatus(`${manifest.chunks.length} chunk を順に構築中…`);

      // Built as chunks arrive, rendered once at the end: a tree that is
      // rebuilt per chunk would spend the whole load in layout.
      const index = new HierarchyIndex();

      const result = await streamModel(this.source, handle, manifest, this.viewer, {
        budget: this.budget,
        signal,
        onChunkLoaded: (chunk) => {
          if (generation !== this.generation) {
            return;
          }
          index.add(chunk);
        },
        onProgress: (progress) => {
          // A superseded load must not repaint the current load's panel.
          if (generation !== this.generation) {
            return;
          }
          this.renderProgress(progress);
        },
      });

      if (generation !== this.generation) {
        return;
      }
      // The tree describes what is on screen, so it is built from the chunks
      // that actually loaded — skipped and failed ones are absent by
      // construction, not filtered out afterwards.
      this.showHierarchy(index.build(result.guidTable));
      this.renderProgress(result);
      el("diag-stream").textContent = result.aborted
        ? "aborted"
        : result.budgetExhausted
          ? `budget reached — ${formatCount(result.skippedChunks)} chunk skipped`
          : "complete";
      const failureNote =
        result.failedChunks > 0 ? `／${result.failedChunks} chunk が構築に失敗しました` : "";
      this.setStatus(
        (result.budgetExhausted
          ? `表示予算に達したため ${result.skippedChunks} chunk を省略しました`
          : "読み込み完了") + failureNote,
      );

      await this.refreshSessionDiagnostics(handle);
      this.updateLoadMetrics();
      this.renderModelStats();
      this.resetSectionBox();
    } catch (cause: unknown) {
      if (generation !== this.generation) {
        // A newer load already owns `this.handle`, the status line and the error
        // box. Closing or reporting here would kill a live session and stomp its
        // UI with a message about a load nobody is waiting for. The newer load's
        // own `closeCurrentSession()` has already released our handle.
        return;
      }
      this.fail(toEngineError("open", cause));
      this.loadStartedAtMs = null;
      this.perfLoadTime.textContent = "—";
      this.perfParseRate.textContent = "—";
      if (opened !== null) {
        if (this.handle === opened) {
          this.handle = null;
        }
        await this.source.close(opened);
      }
    } finally {
      if (generation === this.generation) {
        this.hideLoadProgressOverlay();
      }
    }
  }

  private renderProgress(progress: StreamProgress): void {
    this.lastProgress = progress;
    el("diag-chunks").textContent =
      `${formatCount(progress.loadedChunks)} / ${formatCount(progress.plannedChunks)} loaded` +
      (progress.skippedChunks > 0 ? ` (${formatCount(progress.skippedChunks)} skipped)` : "");
    el("diag-vertices").textContent =
      `${formatCount(progress.estimatedVertices)} / ${formatCount(progress.realVertices)}`;
    el("diag-triangles").textContent = formatCount(progress.triangles);

    // Which decode path the repeated guid_table took. The M2 claim rests on the
    // fast path actually running, so a silent fallback has to be visible: expect
    // exactly one full parse (the first chunk, which keeps the table).
    el("diag-guid-parse").textContent =
      `${formatCount(progress.strippedHeaders)} stripped / ` +
      `${formatCount(progress.fullParseHeaders)} full parse`;

    // Contained per-chunk failures. Shown even when zero so the panel never
    // implies "no failures" merely by omitting the row.
    el("diag-failed").textContent =
      progress.failedChunks === 0
        ? "0"
        : `${formatCount(progress.failedChunks)} — ${progress.firstFailure ?? ""}`;

    this.updateLoadProgressOverlay(progress);
  }

  private showLoadProgressOverlay(): void {
    this.loadProgressOverlay.hidden = false;
    this.loadProgressBar.style.width = "0%";
    this.loadProgressLabel.textContent = "読み込み中… / Loading…";
  }

  private updateLoadProgressOverlay(progress: StreamProgress): void {
    const pct =
      progress.plannedChunks === 0
        ? 0
        : Math.min(100, Math.round((progress.loadedChunks / progress.plannedChunks) * 100));
    this.loadProgressBar.style.width = `${pct}%`;
    this.loadProgressLabel.textContent =
      `読み込み中 ${pct}% / Loading ${pct}% — ` +
      `${formatCount(progress.loadedChunks)} / ${formatCount(progress.plannedChunks)} chunks`;
  }

  private hideLoadProgressOverlay(): void {
    this.loadProgressOverlay.hidden = true;
  }

  private async refreshSessionDiagnostics(handle: SessionHandle): Promise<void> {
    const stats = await this.source.stats(handle);
    el("diag-model-bytes").textContent = formatBytes(stats.model_logical_bytes);
    el("diag-plan-bytes").textContent = formatBytes(stats.plan_logical_bytes);
    el("diag-peak-bytes").textContent = formatBytes(stats.peak_chunk_working_bytes);
    el("diag-peak-proj-bytes").textContent = formatBytes(stats.peak_projection_working_bytes);
    el("diag-live-sessions").textContent = formatCount(stats.live_session_count);
    el("diag-live-models").textContent = formatCount(stats.live_model_count);
    el("diag-live-projections").textContent = formatCount(stats.live_projection_count);
    el("diag-retained-meshes").textContent = formatCount(stats.retained_product_mesh_count);
    el("diag-retained-mesh-bytes").textContent = formatBytes(stats.retained_mesh_bytes);
    el("diag-retained-logical-bytes").textContent = formatBytes(stats.retained_logical_bytes);
    el("diag-cache-budget").textContent = formatBytes(stats.mesh_cache_budget_bytes);
    el("diag-chunk-requests").textContent = formatCount(stats.chunk_requests);

    const linear = await this.source.linearMemoryBytes();
    // A shell that links the engine natively has no linear memory and reports 0.
    // Say so rather than letting a 0 read as "used nothing".
    el("diag-linear-bytes").textContent =
      linear === 0 ? "n/a (no wasm linear memory)" : formatBytes(linear);
    this.lastLinearBytes = linear;
  }

  private updateLoadMetrics(): void {
    if (this.loadStartedAtMs === null) {
      return;
    }
    const durationMs = performance.now() - this.loadStartedAtMs;
    this.perfLoadTime.textContent = `${(durationMs / 1000).toFixed(2)} s`;

    const progress = this.lastProgress;
    if (progress === null || durationMs <= 0) {
      this.perfParseRate.textContent = "—";
      return;
    }
    const seconds = durationMs / 1000;
    const vtxRate = progress.realVertices / seconds;
    const triRate = progress.triangles / seconds;
    this.perfParseRate.textContent =
      `${formatCount(Math.round(vtxRate))} vtx/s · ${formatCount(Math.round(triRate))} tri/s`;
  }

  private resetPerfMeter(): void {
    this.lastRenderedFps = -1;
    this.lastRenderedLinearBytes = -1;
    this.lastLinearBytes = 0;
    this.lastLinearUpdateAt = 0;
    this.linearMemoryPolling = false;
    this.perfLoadTime.textContent = "—";
    this.perfParseRate.textContent = "—";
    this.renderWasmMemory(0);
    this.updatePerfMeter();
  }

  private startPerfLoop(): void {
    const loop = (): void => {
      this.updatePerfMeter();
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  private updatePerfMeter(): void {
    const fps = this.viewer.fps;
    if (fps !== this.lastRenderedFps) {
      this.lastRenderedFps = fps;
      this.perfFps.textContent = `${fps} FPS`;
    }

    const now = performance.now();
    if (!this.linearMemoryPolling && now - this.lastLinearUpdateAt > 1000) {
      this.linearMemoryPolling = true;
      this.lastLinearUpdateAt = now;
      void this.source
        .linearMemoryBytes()
        .then((bytes) => {
          this.lastLinearBytes = bytes;
        })
        .catch(() => {
          // The poll is advisory; a transient failure should not spam the console.
        })
        .finally(() => {
          this.linearMemoryPolling = false;
        });
    }
    if (this.lastLinearBytes !== this.lastRenderedLinearBytes) {
      this.lastRenderedLinearBytes = this.lastLinearBytes;
      this.renderWasmMemory(this.lastLinearBytes);
    }
  }

  /** Render WASM linear memory as a bar against a 50 MiB visual reference line. */
  private renderWasmMemory(bytes: number): void {
    if (bytes === 0) {
      this.perfWasmMemory.textContent = "n/a";
      this.perfWasmBar.style.width = "0%";
      this.perfWasmBar.classList.remove("perf-bar-high");
      return;
    }
    this.perfWasmMemory.textContent = formatBytes(bytes);
    const pct = Math.min(100, (bytes / WASM_MEMORY_REFERENCE_BYTES) * 100);
    this.perfWasmBar.style.width = `${pct.toFixed(1)}%`;
    this.perfWasmBar.classList.toggle("perf-bar-high", bytes > WASM_MEMORY_REFERENCE_BYTES);
  }

  private async closeCurrentSession(): Promise<void> {
    const handle = this.handle;
    const projection = this.planProjection;
    this.handle = null;
    this.planProjection = null;
    this.currentPlanChunkId = null;
    this.planChunkRequestId += 1;

    if (handle !== null) {
      if (projection !== null) {
        await this.source.closePlanProjection(handle, projection.projectionId);
      }
      // A forgotten handle retains its parsed model AND its plan for the life of
      // the page; this is the only thing that frees them.
      await this.source.close(handle);
    }
  }

  setStatus(message: string): void {
    this.status.textContent = message;
  }

  private clearError(): void {
    this.errorBox.hidden = true;
    this.errorBox.textContent = "";
  }

  /**
   * Show a typed failure. The engine's thrown JSON is shown verbatim: the demo
   * never truncates a model silently and never falls back to a partial render
   * without saying so.
   */
  fail(error: IfcEngineError): void {
    this.setStatus("失敗しました");
    this.errorBox.hidden = false;
    this.errorBox.textContent =
      error.raw === "" ? error.message : `${error.message}\n\n${error.raw}`;
  }
}
